import type { CategoryData } from 'src/types/category-data';
import type { TagData } from 'src/types/tag-data';
import { useMemo } from 'react';
import { isNil, uniqBy } from 'es-toolkit';
import TrackedButton from 'src/components/tracked/tracked-button';

import type { AdvisorInfo } from '../../types/advisor-info';
import type { ActivableFilter } from '../utils/filter-by';
import { ActiveCategoriesFilter } from '../../common/filters/active-categories-filter';
import { ActiveChecklistFilter } from '../../common/filters/active-checklist-filter';
import { ActiveFilter } from '../../common/filters/active-filter';
import { ActiveTagsFilter } from '../../common/filters/active-tags-filter';
import { SimpleChecklistFilter } from '../../common/filters/simple-checklist-filter';
import { useClientsUrlState, useClientsUrlStateDispatch } from '../utils/url-state-hooks';

const ACTIVABLE_FILTER_KEYS: ActivableFilter[] = [
  'advisor',
  'reviewDue',
  'servicesDue',
  'lastContactDaysSince',
  'householdTags',
  'householdTagCategories',
  'primaryAddressStates',
];

// For display, we group reviewDue and servicesDue into a single 'serviceCalendar' filter
type DisplayableFilter =
  | 'advisor'
  | 'serviceCalendar'
  | 'lastContactDaysSince'
  | 'householdTags'
  | 'householdTagCategories';

type ClientsActiveFiltersProps = {
  readonly tags?: readonly TagData[];
  readonly categories?: readonly CategoryData[];
  readonly advisorInfo?: AdvisorInfo | null;
};

export function ClientsActiveFilters({ tags, categories, advisorInfo }: ClientsActiveFiltersProps) {
  const state = useClientsUrlState();
  const urlDispatch = useClientsUrlStateDispatch();

  const advisorOptions = useMemo(() => {
    if (isNil(advisorInfo)) {
      return [];
    }

    if (isNil(advisorInfo.advisorGroups)) {
      return [];
    }

    const advisors: { id: string; firstName: string; lastName: string }[] = [];

    // Include all members from advisor groups if viewAdvisorGroupClients is true
    if (advisorInfo.viewAdvisorGroupClients) {
      advisorInfo.advisorGroups.forEach((group) => {
        group.members.forEach((member) => {
          // Only include advisors (not admins or other roles)
          if (member.user.role === 'ADVISOR') {
            advisors.push({
              id: member.user.id,
              firstName: member.user.firstName,
              lastName: member.user.lastName,
            });
          }
        });
      });
    } else {
      // Only show current advisor
      advisors.push({
        id: advisorInfo.user.id,
        firstName: advisorInfo.user.firstName,
        lastName: advisorInfo.user.lastName,
      });
    }

    return uniqBy(advisors, (advisor) => advisor.id).map((advisor) => ({
      label: `${advisor.firstName} ${advisor.lastName}`,
      value: advisor.id,
    }));
  }, [advisorInfo]);

  const hasAnyFiltersApplied = ACTIVABLE_FILTER_KEYS.some((key): boolean => {
    const value = state[key];
    if (value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    return value;
  });

  if (!hasAnyFiltersApplied) {
    return null;
  }

  // Build displayable filters - group service calendar
  const displayableFilters: DisplayableFilter[] = [];
  if (state.advisor && state.advisor.length > 0) {
    displayableFilters.push('advisor');
  }
  if (state.reviewDue || state.servicesDue) {
    displayableFilters.push('serviceCalendar');
  }
  if (state.lastContactDaysSince && state.lastContactDaysSince.length > 0) {
    displayableFilters.push('lastContactDaysSince');
  }
  if (state.householdTags && state.householdTags.length > 0) {
    displayableFilters.push('householdTags');
  }
  if (state.householdTagCategories && state.householdTagCategories.length > 0) {
    displayableFilters.push('householdTagCategories');
  }

  return (
    <div className="flex flex-row flex-wrap gap-2 mt-4">
      {displayableFilters.map((filterBy) => (
        <ClientsActiveFilter
          key={filterBy}
          filterBy={filterBy}
          tags={tags}
          categories={categories}
          advisorOptions={advisorOptions}
        />
      ))}

      {state.primaryAddressStates && state.primaryAddressStates.length > 0 && (
        <ActiveFilter
          label="Primary location"
          values={state.primaryAddressStates}
          onClear={() => urlDispatch({ type: 'setPrimaryAddressStates', primaryAddressStates: undefined })}
        />
      )}

      <TrackedButton
        elementName="clients-table-header-active-filters-clear-all-button"
        onClick={() =>
          urlDispatch({
            type: 'clearAllFilters',
          })
        }
        variant="ghost"
      >
        Clear all
      </TrackedButton>
    </div>
  );
}

type AdvisorOption = {
  readonly label: string;
  readonly value: string;
};

type ClientsActiveFilterProps = {
  readonly filterBy: DisplayableFilter;
  readonly tags?: readonly TagData[];
  readonly categories?: readonly CategoryData[];
  readonly advisorOptions: readonly AdvisorOption[];
};

function ClientsActiveFilter({ filterBy, tags, categories, advisorOptions }: ClientsActiveFilterProps) {
  const urlState = useClientsUrlState();
  const dispatch = useClientsUrlStateDispatch();

  // Special handling for householdTags - render as individual Tag components
  if (filterBy === 'householdTags') {
    const householdTagIds = urlState.householdTags;
    if (!householdTagIds || householdTagIds.length === 0 || !tags) {
      return null;
    }

    // TypeScript can't narrow the generic type, so we extract it here
    const tagIds: readonly string[] = householdTagIds;
    const handleRemoveTag = (tagId: string) => {
      dispatch({
        type: 'setHouseholdTagsFilter',
        householdTags: tagIds.filter((id) => id !== tagId),
      });
    };

    const handleToggleTag = (tagId: string) => {
      const isSelected = tagIds.includes(tagId);
      if (isSelected) {
        handleRemoveTag(tagId);
      } else {
        dispatch({
          type: 'setHouseholdTagsFilter',
          householdTags: [...tagIds, tagId],
        });
      }
    };

    const handleClearAll = () => {
      dispatch({
        type: 'setHouseholdTagsFilter',
        householdTags: [],
      });
    };

    return (
      <ActiveTagsFilter
        tags={tags}
        selectedTagIds={tagIds}
        onRemoveTag={handleRemoveTag}
        onToggleTag={handleToggleTag}
        onClearAll={handleClearAll}
        filterLabel="Tags"
      />
    );
  }

  // Special handling for householdTagCategories - render as individual Category components
  if (filterBy === 'householdTagCategories') {
    const householdTagCategoryIds = urlState.householdTagCategories;
    if (!householdTagCategoryIds || householdTagCategoryIds.length === 0 || !categories) {
      return null;
    }

    // TypeScript can't narrow the generic type, so we extract it here
    const categoryIds: readonly string[] = householdTagCategoryIds;
    const handleRemoveCategory = (categoryId: string) => {
      dispatch({
        type: 'setHouseholdTagCategoriesFilter',
        householdTagCategories: categoryIds.filter((id) => id !== categoryId),
      });
    };

    const handleToggleCategory = (categoryId: string) => {
      const isSelected = categoryIds.includes(categoryId);
      if (isSelected) {
        handleRemoveCategory(categoryId);
      } else {
        dispatch({
          type: 'setHouseholdTagCategoriesFilter',
          householdTagCategories: [...categoryIds, categoryId],
        });
      }
    };

    const handleClearAll = () => {
      dispatch({
        type: 'setHouseholdTagCategoriesFilter',
        householdTagCategories: [],
      });
    };

    return (
      <ActiveCategoriesFilter
        categories={categories}
        selectedCategoryIds={categoryIds}
        onRemoveCategory={handleRemoveCategory}
        onToggleCategory={handleToggleCategory}
        onClearAll={handleClearAll}
        filterLabel="Tag categories"
      />
    );
  }

  // Handle serviceCalendar as a single filter with multiple values
  if (filterBy === 'serviceCalendar') {
    const selectedValues: string[] = [];
    if (urlState.reviewDue) selectedValues.push('reviewDue');
    if (urlState.servicesDue) selectedValues.push('servicesDue');

    if (selectedValues.length === 0) {
      return null;
    }

    const allOptions = [
      { value: 'reviewDue', label: 'Review due' },
      { value: 'servicesDue', label: 'Services due' },
    ];

    const filterContent = (
      <SimpleChecklistFilter
        options={allOptions}
        selectedValues={selectedValues}
        onChangeSelection={(values) => {
          // Use combined action to avoid race conditions
          dispatch({
            type: 'setServiceCalendarFilters',
            reviewDue: values.includes('reviewDue'),
            servicesDue: values.includes('servicesDue'),
          });
        }}
        enableSearch={false}
      />
    );

    return (
      <ActiveChecklistFilter
        allOptions={allOptions}
        selectedValues={selectedValues}
        onRemoveOption={(value) => {
          // Use combined action to avoid race conditions
          dispatch({
            type: 'setServiceCalendarFilters',
            reviewDue: value === 'reviewDue' ? false : !!urlState.reviewDue,
            servicesDue: value === 'servicesDue' ? false : !!urlState.servicesDue,
          });
        }}
        filterLabel="Service calendar"
        filterContent={filterContent}
      />
    );
  }

  if (filterBy === 'lastContactDaysSince') {
    const selectedValues = urlState.lastContactDaysSince ?? [];
    if (selectedValues.length === 0) {
      return null;
    }

    const allOptions = [
      { value: '60', label: '60+ days' },
      { value: '90', label: '90+ days' },
      { value: '180', label: '180+ days' },
      { value: '365', label: '365+ days' },
    ];

    const filterContent = (
      <SimpleChecklistFilter
        options={allOptions}
        selectedValues={selectedValues}
        onChangeSelection={(values) => {
          dispatch({
            type: 'setLastContactDaysSinceFilter',
            lastContactDaysSince: values.length > 0 ? values : undefined,
          });
        }}
        enableSearch={false}
      />
    );

    return (
      <ActiveChecklistFilter
        allOptions={allOptions}
        selectedValues={selectedValues}
        onRemoveOption={(value) => {
          dispatch({
            type: 'setLastContactDaysSinceFilter',
            lastContactDaysSince: selectedValues.filter((selectedValue) => selectedValue !== value),
          });
        }}
        filterLabel="Last contact"
        filterContent={filterContent}
      />
    );
  }

  const advisorValues = urlState.advisor;
  if (!advisorValues || advisorValues.length === 0) {
    return null;
  }

  const advisorIds: readonly string[] = advisorValues;

  // Use the full advisor options list from advisorInfo
  // This ensures all advisors are available in the dropdown, not just selected ones
  const allOptions = advisorOptions.length > 0 ? advisorOptions : advisorIds.map((value) => ({ value, label: value }));

  // Get the selected values that exist in the options
  const selectedValues = advisorIds.filter((id) => allOptions.some((opt) => opt.value === id));

  const handleRemoveAdvisor = (value: string) => {
    dispatch({
      type: 'setAdvisorFilter',
      advisor: advisorIds.filter((v) => v !== value),
    });
  };

  const filterContent = (
    <SimpleChecklistFilter
      options={allOptions}
      selectedValues={selectedValues}
      onChangeSelection={(values) => dispatch({ type: 'setAdvisorFilter', advisor: values })}
      enableSearch={true}
    />
  );

  return (
    <ActiveChecklistFilter
      allOptions={allOptions}
      selectedValues={selectedValues}
      onRemoveOption={handleRemoveAdvisor}
      filterLabel="Primary advisor"
      filterContent={filterContent}
    />
  );
}
